<script setup>
// ---------------------------------------------------------------------------
// The dataset itself, over the full file rather than the slim one the charts use.
//
// Every row, always: the table is not tied to the dashboard's slicers, because
// this page offers the file rather than a reading of it.
//
// The search box writes into the shared filter store, so the query string
// carries it: a public explainer has to let a reader send a link to the view
// they are actually looking at.
//
// ponytail: paging renders thirty rows at a time and the columns still do not
// sort. Sorting is the next thing to add here, once the columns are settled.
// ---------------------------------------------------------------------------

import { computed, ref, watch } from 'vue'

import { detailUrl, toCsv } from '../cases.js'
import { filters } from '../store.js'
import { useJson } from '../useCases.js'

const PAGE = 20

const props = defineProps({
    src: { type: String, required: true },
    searchLabel: { type: String, default: '' },
    // Pipe-separated column headings, in the order the table draws them.
    columnLabels: { type: String, default: '' },
    // Pipe-separated, three Czech forms: one, two to four, five and more.
    countLabel: { type: String, default: '' },
    pageLabel: { type: String, default: '' },
    prevLabel: { type: String, default: '' },
    nextLabel: { type: String, default: '' },
    downloadLabel: { type: String, default: '' },
    fileName: { type: String, default: 'uohs.csv' },
    errorText: { type: String, default: '' }
})

const { data: rows, failed } = useJson(props.src, [])

const page = ref(1)

const columns = computed(() => props.columnLabels.split('|'))

const matching = computed(() => {
    const needle = (filters.q ?? '').trim().toLocaleLowerCase('cs')
    if (!needle) return rows.value

    return rows.value.filter((row) =>
        [row.vec, row.zadavatel, row.sp_zn, row.cpv_category]
            .filter(Boolean)
            .some((field) => field.toLocaleLowerCase('cs').includes(needle))
    )
})

const pages = computed(() => Math.max(1, Math.ceil(matching.value.length / PAGE)))

const shown = computed(() => matching.value.slice((page.value - 1) * PAGE, page.value * PAGE))

// Narrowing the search while on page nine would otherwise leave the reader
// staring at an empty table.
watch(matching, () => {
    page.value = 1
})

/**
 * Hand back exactly what is on screen after filtering, not the whole file.
 *
 * A Blob and an object URL, because there is no server side to ask: the site
 * has no database and the dataset is already in memory.
 */
function download() {
    const blob = new Blob([toCsv(matching.value)], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = url
    link.download = props.fileName
    link.click()

    URL.revokeObjectURL(url)
}

const money = (value) =>
    value === null ? '—' : `${value.toLocaleString('cs-CZ', { maximumFractionDigits: 0 })} Kč`

const day = (value) => (value === null ? '—' : new Date(value).toLocaleDateString('cs-CZ'))

const number = (value) => value.toLocaleString('cs-CZ')

// "1 řádků odpovídá" is the sort of thing a reader stops on, so the count is
// declined. Czech splits at one, at four, and everything above.
const counted = computed(() => {
    const total = matching.value.length
    const forms = props.countLabel.split('|')
    const form = total === 1 ? forms[0] : total < 5 ? forms[1] : forms[2]

    return `${number(total)} ${form ?? forms[0] ?? ''}`.trim()
})
</script>

<template>
    <p v-if="failed" class="chart__failed">{{ errorText }}</p>

    <div v-else class="datatable">
        <label class="datatable__search">
            <span>{{ searchLabel }}</span>
            <input v-model="filters.q" type="search" class="form-control">
        </label>

        <div class="datatable__bar">
            <p v-if="rows.length" class="chart__base">
                {{ counted }}
            </p>

            <nav v-if="pages > 1" class="datatable__pages">
                <button
                    type="button"
                    class="btn btn-outline-primary btn-sm"
                    :disabled="page === 1"
                    @click="page -= 1"
                >{{ prevLabel }}</button>

                <span>{{ pageLabel }} {{ number(page) }} / {{ number(pages) }}</span>

                <button
                    type="button"
                    class="btn btn-outline-primary btn-sm"
                    :disabled="page === pages"
                    @click="page += 1"
                >{{ nextLabel }}</button>
            </nav>
        </div>

        <div class="datatable__scroll">
            <table class="table">
                <thead>
                    <tr>
                        <th v-for="column in columns" :key="column" scope="col">{{ column }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="row in shown" :key="`${row.id}-${row.cena}`">
                        <td><a :href="detailUrl(row.id)" rel="noopener">{{ row.sp_zn }}</a></td>
                        <td>{{ row.vec }}</td>
                        <td>{{ row.zadavatel ?? '—' }}</td>
                        <td>{{ row.typ_zahajeni_rizeni ?? '—' }}</td>
                        <td class="datatable__num">{{ money(row.cena) }}</td>
                        <td class="datatable__num">{{ day(row.datum_pravni_moci) }}</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="datatable__footer">
            <button
                type="button"
                class="btn btn-outline-primary"
                :disabled="!matching.length"
                @click="download"
            >{{ downloadLabel }}</button>
        </div>
    </div>
</template>
